import { useEffect } from 'react';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  KEYWORD_PHRASES,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_URL,
  OG_IMAGE_WIDTH,
  SITE_NAME,
  SITE_URL,
} from '../config/seo';

function upsertMeta(attr, key, value) {
  const selector = attr === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertHreflangEn(href) {
  let el = document.head.querySelector('link[rel="alternate"][hreflang="en"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', 'en');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Syncs document metadata from `src/config/seo.js` on each load so copy stays maintainable
 * and you can extend this later (e.g. per-route titles if you add a router).
 */
export default function SeoHead() {
  useEffect(() => {
    document.title = DEFAULT_TITLE;

    upsertMeta('name', 'description', DEFAULT_DESCRIPTION);
    upsertMeta('name', 'keywords', KEYWORD_PHRASES.join(', '));
    upsertMeta('name', 'author', SITE_NAME);
    upsertMeta(
      'name',
      'robots',
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    );
    upsertMeta('name', 'googlebot', 'index, follow');
    upsertMeta('name', 'bingbot', 'index, follow');
    upsertMeta('name', 'language', 'English');
    upsertMeta('name', 'geo.region', 'NG');
    upsertMeta('name', 'geo.placename', 'Nigeria');
    upsertMeta('name', 'distribution', 'global');
    upsertMeta('name', 'referrer', 'strict-origin-when-cross-origin');

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', SITE_URL);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', DEFAULT_TITLE);
    upsertMeta('property', 'og:description', DEFAULT_DESCRIPTION);
    upsertMeta('property', 'og:image', OG_IMAGE_URL);
    upsertMeta('property', 'og:image:width', String(OG_IMAGE_WIDTH));
    upsertMeta('property', 'og:image:height', String(OG_IMAGE_HEIGHT));
    upsertMeta('property', 'og:image:type', 'image/png');
    upsertMeta('property', 'og:image:alt', `${SITE_NAME} — logo and wordmark`);
    upsertMeta('property', 'og:locale', 'en_US');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', DEFAULT_TITLE);
    upsertMeta('name', 'twitter:description', DEFAULT_DESCRIPTION);
    upsertMeta('name', 'twitter:image', OG_IMAGE_URL);

    upsertCanonical(SITE_URL);
    upsertHreflangEn(SITE_URL);
  }, []);

  return null;
}
